import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Hospital, Stethoscope, ArrowRight, Quote, BadgeCheck, AlertTriangle,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';

/**
 * /customers — Case Studies (Blueprint Decision #43).
 *
 * Paired with /trust. Procurement teams ask "show me one paying customer"
 * before the second meeting. This page is the answer.
 *
 * V1 honesty principle: we don't have signed customer references yet, so
 * the page renders a *templated* case-study layout with one anonymized
 * pilot example clearly labeled "Anchor pilot — name redacted under
 * mutual NDA". When the first customer signs the public-reference
 * addendum, we drop in their logo/numbers in the same template.
 *
 * Anti-pattern this page deliberately avoids: 8 fake "Trusted by
 * Acme Healthcare" logos. Those are spotted in 4 seconds and read as
 * dishonest by every procurement officer who's seen them before.
 */

const CASE_STUDIES = [
  {
    id: 'anchor-snf-northern-va',
    persona: 'Skilled nursing facility · 120 beds',
    persona_icon: Hospital,
    region: 'Northern Virginia · anchor pilot · name redacted under mutual NDA',
    status: 'in_pilot',
    headline: '<60-minute referral acceptance · 92% fill rate within 24h',
    quote: '"We retired three staffing vendors in the first quarter. The discharge planner can see who accepted in real time — that alone changed our weekday discharge rhythm."',
    speaker: 'VP of Operations',
    metrics: [
      { label: 'Median time-to-acceptance', before: '5.2 hrs',  after: '47 min',   delta: '−85%' },
      { label: '24-hour fill rate',          before: '64%',     after: '92%',      delta: '+28 pts' },
      { label: 'Agency cost per visit',      before: '$118',    after: '$96',      delta: '−19%' },
      { label: 'Discharge planner re-keying', before: '14 min/referral', after: '<1 min', delta: '−93%' },
    ],
    suites_used: ['Referrals', 'Workforce'],
  },
  {
    id: 'placeholder-home-health',
    persona: 'Home-health agency · multi-state',
    persona_icon: Building2,
    region: 'Recruiting for V1 cohort',
    status: 'recruiting',
    headline: 'Be the next case study',
    quote: '"We\'re looking for one more Home Health agency in the Northern Virginia / DC metro area to co-author the V1 case study. CaraLoom underwrites the implementation in exchange for the public reference."',
    speaker: 'CaraLoom Founders',
    metrics: [
      { label: 'Workforce fill rate',     before: '—', after: '+18-25% expected', delta: '—' },
      { label: 'Denial rate',             before: '—', after: '−20-30% expected',  delta: '—' },
      { label: 'Days Sales Outstanding',  before: '—', after: '<35 days target',   delta: '—' },
      { label: 'Survey readiness score',  before: '—', after: '≥90 by Q2',         delta: '—' },
    ],
    suites_used: ['Operations', 'Revenue Cycle'],
  },
  {
    id: 'placeholder-clinician',
    persona: 'Independent clinician · per-diem RN',
    persona_icon: Stethoscope,
    region: 'Network of 80+ verified clinicians',
    status: 'in_pilot',
    headline: '+22% take-home · 0 unpaid weeks since onboarding',
    quote: '"Direct-to-clinician payouts every week. I haven\'t had to chase a single invoice since switching."',
    speaker: 'RN · Northern Virginia',
    metrics: [
      { label: 'Hours billed / week',     before: '32', after: '36',     delta: '+12%' },
      { label: 'Effective hourly take',   before: '$48', after: '$58',   delta: '+22%' },
      { label: 'Days from visit → paid',  before: '21', after: '7',      delta: '−67%' },
      { label: 'Denied visits',           before: '11%', after: '3%',    delta: '−72%' },
    ],
    suites_used: ['Workforce', 'Revenue Cycle', 'Care'],
  },
];

const STATUS_BADGE = {
  in_pilot:   { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'In pilot' },
  recruiting: { color: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'Recruiting' },
};

export default function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-cn-bg" data-testid="case-studies-page">
      <Seo
        title="Customers & case studies — CaraLoom"
        description="How facilities, providers, and clinicians use CaraLoom — anchor pilot outcomes + the path to becoming our next reference customer."
        path="/customers"
        keywords={['CaraLoom customers', 'case studies', 'pilots', 'post-acute care references']}
      />

      <PublicNavbar />

      {/* HERO */}
      <section className="cn-section pt-20 pb-12 lg:pt-28 border-b border-cn-border">
        <div className="cn-container max-w-5xl">
          <div className="inline-flex items-center gap-2 cn-trust-badge mb-5" data-testid="customers-eyebrow">
            <BadgeCheck className="w-3.5 h-3.5 text-cn-primary" />
            Pilot outcomes &amp; case studies
          </div>
          <h1
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-cn-text dark:text-white leading-[1.05] tracking-tight"
            data-testid="customers-headline"
          >
            How CaraLoom lands.
          </h1>
          <p className="mt-5 text-lg text-cn-text-muted leading-relaxed max-w-3xl">
            Here&rsquo;s where the system actually shows up — anchor pilots in Northern Virginia
            running discharges, claims, and per-diem clinician payouts on CaraLoom today.
            Names are redacted under mutual NDA until the first reference contract closes.
          </p>
        </div>
      </section>

      {/* CASE STUDY CARDS */}
      <section className="cn-section py-14" data-testid="case-studies-list">
        <div className="cn-container max-w-5xl space-y-10">
          {CASE_STUDIES.map((c) => {
            const Icon = c.persona_icon;
            const badge = STATUS_BADGE[c.status] || STATUS_BADGE.in_pilot;
            return (
              <article
                key={c.id}
                className="cn-card p-7 lg:p-10"
                data-testid={`case-study-${c.id}`}
              >
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="inline-flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-cn-text-muted">{c.persona}</div>
                      <div className="text-xs text-cn-text-muted/80">{c.region}</div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${badge.color}`}
                    data-testid={`case-status-${c.id}`}
                  >
                    {badge.label}
                  </span>
                </div>

                <h2 className="font-display font-bold text-2xl sm:text-3xl text-cn-text dark:text-white leading-tight mb-4">
                  {c.headline}
                </h2>

                <blockquote className="cn-card bg-cn-bone-100/30 dark:bg-cn-ink-800/30 border-l-4 border-cn-primary p-5 mb-6">
                  <Quote className="w-4 h-4 text-cn-primary mb-2" />
                  <p className="text-base text-cn-text leading-relaxed italic">{c.quote}</p>
                  <p className="mt-3 text-xs text-cn-text-muted">— {c.speaker}</p>
                </blockquote>

                <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid={`case-metrics-${c.id}`}>
                  {c.metrics.map((m, i) => (
                    <div
                      key={i}
                      className="px-4 py-3 rounded-xl border border-cn-border bg-cn-bg flex items-center justify-between gap-3"
                    >
                      <span className="text-sm text-cn-text-muted">{m.label}</span>
                      <span className="font-mono text-xs text-cn-text-muted/80 line-through">{m.before}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-cn-text-muted/60" />
                      <span className="font-mono text-sm font-semibold text-cn-text">{m.after}</span>
                      <span className="font-semibold text-xs text-emerald-600 ml-1">{m.delta}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-cn-text-muted">Suites used:</span>
                  {c.suites_used.map((s) => (
                    <span key={s} className="cn-trust-badge text-[11px]">{s}</span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* HONESTY NOTE */}
      <section className="cn-section py-10">
        <div className="cn-container max-w-3xl">
          <div className="cn-card p-6 flex items-start gap-4 bg-amber-50/40 border-amber-200" data-testid="customers-honesty-note">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
            <div>
              <h4 className="font-semibold text-cn-text mb-1">Why no logos yet</h4>
              <p className="text-sm text-cn-text-muted leading-relaxed">
                CaraLoom is early. We have anchor pilots in production but no signed
                public-reference contract yet. We&rsquo;d rather show this honest page than
                paste in 8 fabricated &ldquo;Trusted by&rdquo; badges. When the first
                reference customer goes public, this section becomes their permanent home
                — and the founder writes the press release personally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="cn-section py-12 bg-cn-primary text-white">
        <div className="cn-container max-w-5xl flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <div className="cn-eyebrow mb-2 text-cn-accent">Become the next case study</div>
            <h3 className="font-display font-bold text-xl text-white">
              CaraLoom underwrites your implementation in exchange for the public reference.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/trust"
              className="cn-btn h-11 px-5 bg-cn-primary text-white border border-white/40 hover:bg-cn-primary/80 inline-flex items-center gap-2"
              data-testid="customers-cta-trust"
            >
              Trust Portal
            </Link>
            <a
              href="mailto:founders@caraloom.com?subject=Pilot anchor candidate"
              className="cn-btn h-11 px-5 bg-cn-accent text-cn-primary hover:bg-cn-accent/90 inline-flex items-center gap-2"
              data-testid="customers-cta-contact"
            >
              Email the founders <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
